import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDateTime } from '../../utils/dateUtils';
import ConfirmModal from '../common/ConfirmModal';
import MiniCalendar from './MiniCalendar';
import TravelTimeInfo from './TravelTimeInfo';

const DriverRideRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [dayBookingRequests, setDayBookingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedDayBooking, setSelectedDayBooking] = useState(null);
  const [bufferTimes, setBufferTimes] = useState({});
  const [dayBookingBufferTimes, setDayBookingBufferTimes] = useState({});
  const [declineConfirm, setDeclineConfirm] = useState({ isOpen: false, id: null, type: null });

  // Calendar and travel time state (for regular rides)
  const [expandedCalendars, setExpandedCalendars] = useState({});
  const [calendarData, setCalendarData] = useState({});
  const [travelTimeData, setTravelTimeData] = useState({});
  const [calendarLoading, setCalendarLoading] = useState({});
  const [travelTimeLoading, setTravelTimeLoading] = useState({});
  const [travelTimeErrors, setTravelTimeErrors] = useState({});
  const [driverTier, setDriverTier] = useState(null);

  // Calendar and travel time state (for day bookings)
  const [expandedDayBookingCalendars, setExpandedDayBookingCalendars] = useState({});
  const [dayBookingCalendarData, setDayBookingCalendarData] = useState({});
  const [dayBookingTravelTimeData, setDayBookingTravelTimeData] = useState({});
  const [dayBookingCalendarLoading, setDayBookingCalendarLoading] = useState({});
  const [dayBookingTravelTimeLoading, setDayBookingTravelTimeLoading] = useState({});
  const [dayBookingTravelTimeErrors, setDayBookingTravelTimeErrors] = useState({});

  // Legal confirmation modal state
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [pendingAcceptId, setPendingAcceptId] = useState(null);
  const [pendingAcceptType, setPendingAcceptType] = useState(null); // 'ride' or 'dayBooking'

  useEffect(() => {
    fetchRideRequests();
    fetchDayBookingRequests();
    fetchDriverProfile();
  }, []);

  const fetchDriverProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get tier level from subscription.tier.tier_level
      const tierLevel = response.data.subscription?.tier?.tier_level || 5;
      setDriverTier(tierLevel);
    } catch (error) {
      console.error('Error fetching driver profile:', error);
      setDriverTier(5); // Default to free tier on error
    }
  };

  const toggleCalendar = async (requestId) => {
    const isExpanded = expandedCalendars[requestId];

    if (isExpanded) {
      // Collapse
      setExpandedCalendars(prev => ({ ...prev, [requestId]: false }));
    } else {
      // Expand and fetch data
      setExpandedCalendars(prev => ({ ...prev, [requestId]: true }));

      // Fetch calendar data if not already loaded
      if (!calendarData[requestId]) {
        await fetchCalendarWindow(requestId);
      }

      // Fetch travel times if not already loaded (and not free tier)
      if (!travelTimeData[requestId] && driverTier !== 5) {
        await fetchTravelTimes(requestId);
      }
    }
  };

  const fetchCalendarWindow = async (requestId) => {
    setCalendarLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rides/driver/requests/${requestId}/calendar-window`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCalendarData(prev => ({ ...prev, [requestId]: response.data }));
    } catch (error) {
      console.error('Error fetching calendar window:', error);
    } finally {
      setCalendarLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const fetchTravelTimes = async (requestId) => {
    setTravelTimeLoading(prev => ({ ...prev, [requestId]: true }));
    setTravelTimeErrors(prev => ({ ...prev, [requestId]: null }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rides/driver/requests/${requestId}/travel-times`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTravelTimeData(prev => ({ ...prev, [requestId]: response.data }));
    } catch (error) {
      console.error('Error fetching travel times:', error);
      if (error.response?.status === 403) {
        setTravelTimeErrors(prev => ({ ...prev, [requestId]: 'Travel time calculation is only available for premium tiers' }));
      } else {
        setTravelTimeErrors(prev => ({ ...prev, [requestId]: 'Unable to calculate travel times' }));
      }
    } finally {
      setTravelTimeLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  // Day booking calendar functions
  const toggleDayBookingCalendar = async (bookingId) => {
    const isExpanded = expandedDayBookingCalendars[bookingId];

    if (isExpanded) {
      // Collapse
      setExpandedDayBookingCalendars(prev => ({ ...prev, [bookingId]: false }));
    } else {
      // Expand and fetch data
      setExpandedDayBookingCalendars(prev => ({ ...prev, [bookingId]: true }));

      // Fetch calendar data if not already loaded
      if (!dayBookingCalendarData[bookingId]) {
        await fetchDayBookingCalendarWindow(bookingId);
      }

      // Fetch travel times if not already loaded (and not free tier)
      if (!dayBookingTravelTimeData[bookingId] && driverTier !== 5) {
        await fetchDayBookingTravelTimes(bookingId);
      }
    }
  };

  const fetchDayBookingCalendarWindow = async (bookingId) => {
    setDayBookingCalendarLoading(prev => ({ ...prev, [bookingId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/requests/${bookingId}/calendar-window`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDayBookingCalendarData(prev => ({ ...prev, [bookingId]: response.data }));
    } catch (error) {
      console.error('Error fetching day booking calendar window:', error);
    } finally {
      setDayBookingCalendarLoading(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const fetchDayBookingTravelTimes = async (bookingId) => {
    setDayBookingTravelTimeLoading(prev => ({ ...prev, [bookingId]: true }));
    setDayBookingTravelTimeErrors(prev => ({ ...prev, [bookingId]: null }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/requests/${bookingId}/travel-times`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDayBookingTravelTimeData(prev => ({ ...prev, [bookingId]: response.data }));
    } catch (error) {
      console.error('Error fetching day booking travel times:', error);
      if (error.response?.status === 403) {
        setDayBookingTravelTimeErrors(prev => ({ ...prev, [bookingId]: 'Travel time calculation is only available for premium tiers' }));
      } else {
        setDayBookingTravelTimeErrors(prev => ({ ...prev, [bookingId]: 'Unable to calculate travel times' }));
      }
    } finally {
      setDayBookingTravelTimeLoading(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const fetchRideRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rides/driver/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter to only show pending and counter_offered requests
      const activeRequests = response.data.filter(r =>
        r.status === 'pending' || r.status === 'counter_offered'
      );

      setRequests(activeRequests);

      // Initialize buffer times for each request
      const initialBuffers = {};
      activeRequests.forEach(req => {
        initialBuffers[req.id] = {
          pre_ride_buffer_minutes: 0,
          post_ride_buffer_minutes: 0
        };
      });
      setBufferTimes(initialBuffers);
    } catch (error) {
      console.error('Error fetching ride requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDayBookingRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/day-bookings/driver/pending-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Extract requests array from response wrapper
      const requests = response.data.requests || [];
      setDayBookingRequests(requests);

      // Initialize buffer times for each day booking request
      const initialBuffers = {};
      requests.forEach(booking => {
        initialBuffers[booking.id] = {
          pre_buffer_minutes: 0,
          post_buffer_minutes: 0
        };
      });
      setDayBookingBufferTimes(initialBuffers);
    } catch (error) {
      console.error('Error fetching day booking requests:', error);
    }
  };

  const handleBufferChange = (requestId, field, value) => {
    setBufferTimes(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [field]: parseInt(value) || 0
      }
    }));
  };

  const handleAccept = (requestId) => {
    setPendingAcceptId(requestId);
    setPendingAcceptType('ride');
    setShowAcceptModal(true);
  };

  const confirmAccept = async () => {
    setShowAcceptModal(false);
    const id = pendingAcceptId;
    const type = pendingAcceptType;
    setPendingAcceptId(null);
    setPendingAcceptType(null);

    if (type === 'ride') {
      try {
        const token = localStorage.getItem('token');
        const buffers = bufferTimes[id] || { pre_ride_buffer_minutes: 0, post_ride_buffer_minutes: 0 };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/rides/driver/requests/${id}/respond`,
          {
            status: 'accepted',
            pre_buffer_minutes: buffers.pre_ride_buffer_minutes,
            post_buffer_minutes: buffers.post_ride_buffer_minutes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.error) {
          window.alert(response.data.error + '\n\n' + response.data.action_required);
        } else {
          window.alert('Ride accepted successfully!');
          fetchRideRequests();
        }
      } catch (error) {
        console.error('Error accepting ride:', error);
        if (error.response?.status === 409) {
          const conflictMessage = error.response?.data?.detail || 'You have a scheduling conflict with this ride.';
          window.alert(`Cannot accept ride:\n\n${conflictMessage}\n\nPlease check your schedule and try again.`);
        } else if (error.response?.data?.error) {
          window.alert(error.response.data.error);
        } else if (error.response?.data?.detail) {
          window.alert(error.response.data.detail);
        } else {
          window.alert('Failed to accept ride. Please try again.');
        }
      }
    } else if (type === 'dayBooking') {
      try {
        const token = localStorage.getItem('token');
        const buffers = dayBookingBufferTimes[id] || { pre_buffer_minutes: 0, post_buffer_minutes: 0 };

        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/requests/${id}/accept`,
          {
            pre_buffer_minutes: buffers.pre_buffer_minutes,
            post_buffer_minutes: buffers.post_buffer_minutes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        window.alert('Day booking accepted successfully!');
        fetchDayBookingRequests();
      } catch (error) {
        console.error('Error accepting day booking:', error);
        if (error.response?.status === 409) {
          const conflictMessage = error.response?.data?.detail || 'You have a scheduling conflict with this day booking.';
          window.alert(`Cannot accept day booking:\n\n${conflictMessage}\n\nPlease check your schedule and try again.`);
        } else if (error.response?.data?.detail) {
          window.alert(error.response.data.detail);
        } else {
          window.alert('Failed to accept day booking. Please try again.');
        }
      }
    }
  };

  const handleDecline = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/driver/requests/${requestId}/respond`,
        { status: 'declined' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Ride declined');
      fetchRideRequests();
    } catch (error) {
      console.error('Error declining ride:', error);
      if (error.response?.data?.detail) {
        window.alert(error.response.data.detail);
      } else {
        window.alert('Failed to decline ride');
      }
    }
  };

  const handleCounterOffer = async (requestId, currentPrice) => {
    const newPrice = prompt(
      `Current price: $${parseFloat(currentPrice).toFixed(2)}\n\nEnter your counter-offer price.\nNumbers only, no dollar signs. Decimals are OK (e.g. 100 or 99.50):`,
      currentPrice
    );
    if (!newPrice) return;

    const parsed = parseFloat(newPrice);
    if (isNaN(parsed) || parsed <= 0) {
      window.alert('Please enter a valid price. Use numbers only (e.g. 100 or 99.50). No dollar signs or other characters.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rides/driver/requests/${requestId}/respond`,
        {
          status: 'counter_offer',
          counter_offer_price: parsed
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Counter offer sent!');
      fetchRideRequests();
    } catch (error) {
      console.error('Error sending counter offer:', error);
      if (error.response?.data?.detail) {
        window.alert(error.response.data.detail);
      } else {
        window.alert('Failed to send counter offer');
      }
    }
  };

  const toggleBufferSection = (requestId) => {
    setSelectedRequest(selectedRequest === requestId ? null : requestId);
  };

  const toggleDayBookingBufferSection = (bookingId) => {
    setSelectedDayBooking(selectedDayBooking === bookingId ? null : bookingId);
  };

  const handleDayBookingBufferChange = (bookingId, field, value) => {
    setDayBookingBufferTimes(prev => ({
      ...prev,
      [bookingId]: {
        ...prev[bookingId],
        [field]: parseInt(value) || 0
      }
    }));
  };

  const handleDayBookingAccept = (bookingId) => {
    setPendingAcceptId(bookingId);
    setPendingAcceptType('dayBooking');
    setShowAcceptModal(true);
  };

  const handleDayBookingDecline = async (bookingId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/requests/${bookingId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Day booking declined');
      fetchDayBookingRequests();
    } catch (error) {
      console.error('Error declining day booking:', error);
      if (error.response?.data?.detail) {
        window.alert(error.response.data.detail);
      } else {
        window.alert('Failed to decline day booking');
      }
    }
  };

  const handleDayBookingCounterOffer = async (bookingId, currentFlatRate) => {
    const newRate = window.prompt(
      `Current flat rate: $${parseFloat(currentFlatRate).toFixed(2)}\n\nEnter your counter-offer flat rate.\nNumbers only, no dollar signs. Decimals are OK (e.g. 100 or 99.50):`,
      currentFlatRate
    );
    if (!newRate) return;

    const parsedRate = parseFloat(newRate);
    if (isNaN(parsedRate) || parsedRate <= 0) {
      window.alert('Please enter a valid price. Use numbers only (e.g. 100 or 99.50). No dollar signs or other characters.');
      return;
    }

    const reason = window.prompt('Reason for counter-offer (optional):');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/day-bookings/driver/requests/${bookingId}/counter-offer`,
        {
          counter_offer_flat_rate: parsedRate,
          counter_offer_reason: reason || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.alert('Counter-offer sent successfully!');
      fetchDayBookingRequests();
    } catch (error) {
      console.error('Error sending counter-offer:', error);
      if (error.response?.data?.detail) {
        window.alert(error.response.data.detail);
      } else {
        window.alert('Failed to send counter-offer. Please try again.');
      }
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Legal Confirmation Modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Confirm Ride Acceptance</h3>
            <div className="text-sm text-gray-700 space-y-3 mb-6">
              <p>By accepting this ride, you confirm that:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>You hold a valid driver's license and all permits required by your state and local jurisdiction (as well as those you may travel through) to provide rideshare services.</li>
                <li>You are covered by appropriate automobile insurance that meets or exceeds your jurisdiction's minimum requirements (as well as any you may travel through) for rideshare drivers.</li>
                <li>Your vehicle is in safe operating condition and meets all applicable inspection requirements.</li>
                <li>You will comply with all applicable federal, state, and local laws while providing this ride.</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowAcceptModal(false); setPendingAcceptId(null); setPendingAcceptType(null); }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAccept}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                I Confirm & Accept
              </button>
            </div>
          </div>
        </div>
      )}

      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Ride & Day Booking Requests</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Day Booking Requests Section */}
        {dayBookingRequests.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">📅 Day Booking Requests</h2>
            <div className="space-y-4">
              {dayBookingRequests.map(booking => (
                <div key={booking.id} className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg shadow-lg p-6 border-2 border-orange-200">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      {booking.rider_name && (
                        <div className="text-base font-medium text-blue-600 mb-2">
                          👤 Rider: {booking.rider_name}
                        </div>
                      )}
                      <h3 className="text-lg font-bold mb-2 text-orange-800">
                        {booking.rate_type === 'half_day' ? '🌅 Half-Day Booking (6 hours)' : '☀️ Full-Day Booking (12 hours)'}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-700">
                        <div>📅 Date: {new Date(booking.start_time.endsWith('Z') ? booking.start_time : booking.start_time + 'Z').toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric',
                          
                        })} {new Date(booking.start_time.endsWith('Z') ? booking.start_time : booking.start_time + 'Z').toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                          
                        })} CT → {new Date(booking.end_time.endsWith('Z') ? booking.end_time : booking.end_time + 'Z').toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                          
                        })} CT</div>
                        <div>⏱️ Duration: {booking.duration_hours} hours</div>
                        <div>📏 Total Distance: {booking.total_mileage} miles (Tier {booking.mileage_tier_used})</div>
                        <div>🚗 {booking.legs.length} Trip{booking.legs.length !== 1 ? 's' : ''}</div>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-lg">💰 Your Payout <span className="text-xs font-normal">(net of tax)</span>:</span>
                          <span className="text-2xl font-bold text-green-600">
                            ${booking.driver_payout}
                          </span>
                          <span className="text-xs text-gray-600">(Rider pays: ${booking.rider_total})</span>
                        </div>
                      </div>

                      {/* Show all legs */}
                      <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
                        <div className="text-sm font-semibold text-gray-700 mb-2">Trip Details:</div>
                        {booking.legs.map((leg, index) => (
                          <div key={index} className="text-sm text-gray-600 mb-2 pl-2 border-l-2 border-orange-300">
                            <div className="font-medium">Trip {leg.sequence_number}:</div>
                            <div>📍 From: {leg.pickup_address}</div>
                            <div>📍 To: {leg.dropoff_address}</div>
                            <div>📏 {leg.distance_miles} miles</div>
                            {index < booking.legs.length - 1 && <div className="text-xs text-gray-500 mt-1">(Auto-completes 30 min after pickup)</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="px-3 py-1 rounded text-sm font-medium bg-orange-100 text-orange-800">
                      PENDING
                    </div>
                  </div>

                  {/* Buffer Time Section */}
                  <div className="mb-4 border-t pt-4">
                    <button
                      onClick={() => toggleDayBookingBufferSection(booking.id)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <span className="text-sm font-medium text-gray-700">
                        ⏱️ Set Buffer Times {selectedDayBooking === booking.id ? '▼' : '▶'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {dayBookingBufferTimes[booking.id]?.pre_buffer_minutes > 0 || dayBookingBufferTimes[booking.id]?.post_buffer_minutes > 0
                          ? `Pre: ${dayBookingBufferTimes[booking.id]?.pre_buffer_minutes}min, Post: ${dayBookingBufferTimes[booking.id]?.post_buffer_minutes}min`
                          : 'Optional - Click to set'
                        }
                      </span>
                    </button>

                    {selectedDayBooking === booking.id && (
                      <div className="mt-3 p-4 bg-white rounded-lg">
                        <p className="text-xs text-gray-600 mb-3">
                          Buffer times block your schedule before and after this day booking.
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pre-booking Buffer</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max="120"
                                value={dayBookingBufferTimes[booking.id]?.pre_buffer_minutes || 0}
                                onChange={(e) => handleDayBookingBufferChange(booking.id, 'pre_buffer_minutes', e.target.value)}
                                className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                                placeholder="0"
                              />
                              <span className="text-sm text-gray-600">min</span>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Post-booking Buffer</label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                max="120"
                                value={dayBookingBufferTimes[booking.id]?.post_buffer_minutes || 0}
                                onChange={(e) => handleDayBookingBufferChange(booking.id, 'post_buffer_minutes', e.target.value)}
                                className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-orange-500"
                                placeholder="0"
                              />
                              <span className="text-sm text-gray-600">min</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Schedule Preview Section for Day Bookings */}
                  <div className="mb-4 border-t pt-4">
                    <button
                      onClick={() => toggleDayBookingCalendar(booking.id)}
                      className="flex items-center justify-between w-full text-left px-4 py-2 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg hover:from-orange-100 hover:to-yellow-100 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        📅 {expandedDayBookingCalendars[booking.id] ? 'Hide' : 'Show'} Schedule Preview
                      </span>
                      <span className="text-gray-500">
                        {expandedDayBookingCalendars[booking.id] ? '▼' : '▶'}
                      </span>
                    </button>

                    {expandedDayBookingCalendars[booking.id] && (
                      <div className="mt-4">
                        {/* Calendar View */}
                        <div className="mb-4">
                          {dayBookingCalendarLoading[booking.id] ? (
                            <div className="text-center py-4">
                              <p className="text-sm text-gray-600">Loading schedule...</p>
                            </div>
                          ) : dayBookingCalendarData[booking.id] ? (
                            <MiniCalendar
                              events={dayBookingCalendarData[booking.id].events}
                              timeWindow={dayBookingCalendarData[booking.id].time_window}
                            />
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              Unable to load schedule preview
                            </div>
                          )}
                        </div>

                        {/* Travel Time Info */}
                        {driverTier !== 5 && (
                          <div className="mt-3">
                            {dayBookingTravelTimeLoading[booking.id] ? (
                              <div className="text-center py-2">
                                <p className="text-xs text-gray-500">Calculating travel times...</p>
                              </div>
                            ) : dayBookingTravelTimeErrors[booking.id] ? (
                              <div className="text-center py-2">
                                <p className="text-xs text-gray-400">{dayBookingTravelTimeErrors[booking.id]}</p>
                              </div>
                            ) : dayBookingTravelTimeData[booking.id] ? (
                              <div className="bg-orange-50 rounded-lg p-3">
                                <h4 className="text-xs font-semibold text-gray-700 mb-2">Travel Times to Adjacent Events</h4>
                                {dayBookingTravelTimeData[booking.id].before ? (
                                  <div className="text-xs text-gray-600 mb-2">
                                    <span className="font-medium">From previous {dayBookingTravelTimeData[booking.id].before.event_type}:</span>{' '}
                                    {dayBookingTravelTimeData[booking.id].before.duration_minutes != null ? (
                                      <span className="text-green-700 font-medium">
                                        {dayBookingTravelTimeData[booking.id].before.duration_minutes} min ({dayBookingTravelTimeData[booking.id].before.distance_miles} mi)
                                      </span>
                                    ) : dayBookingTravelTimeData[booking.id].before.error ? (
                                      <span className="text-red-500">{dayBookingTravelTimeData[booking.id].before.error}</span>
                                    ) : (
                                      <span className="text-gray-400">{dayBookingTravelTimeData[booking.id].before.note || 'Event found'}</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400 mb-2">No events within 6 hours before</div>
                                )}
                                {dayBookingTravelTimeData[booking.id].after ? (
                                  <div className="text-xs text-gray-600">
                                    <span className="font-medium">To next {dayBookingTravelTimeData[booking.id].after.event_type}:</span>{' '}
                                    {dayBookingTravelTimeData[booking.id].after.duration_minutes != null ? (
                                      <span className="text-green-700 font-medium">
                                        {dayBookingTravelTimeData[booking.id].after.duration_minutes} min ({dayBookingTravelTimeData[booking.id].after.distance_miles} mi)
                                      </span>
                                    ) : dayBookingTravelTimeData[booking.id].after.error ? (
                                      <span className="text-red-500">{dayBookingTravelTimeData[booking.id].after.error}</span>
                                    ) : (
                                      <span className="text-gray-400">{dayBookingTravelTimeData[booking.id].after.note || 'Event found'}</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-400">No events within 6 hours after</div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {booking.status === 'pending' && booking.counter_offer_flat_rate ? (
                      // Counter offer was accepted by rider - show confirmation UI
                      <>
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                            <span className="text-lg">✓</span>
                            <span className="font-medium">Counter Offer Accepted!</span>
                            <span className="text-sm">
                              Rider agreed to ${parseFloat(booking.counter_offer_flat_rate).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleDayBookingAccept(booking.id)}
                              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold"
                            >
                              ✓ Confirm & Accept Booking
                            </button>
                            <button
                              onClick={() => setDeclineConfirm({ isOpen: true, id: booking.id, type: 'dayBooking' })}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              ✗ Decline
                            </button>
                          </div>
                        </div>
                      </>
                    ) : booking.status === 'pending' ? (
                      // New pending request - show accept/counter/decline options
                      <>
                        <button
                          onClick={() => handleDayBookingAccept(booking.id)}
                          className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                        >
                          ✓ Accept Day Booking
                        </button>
                        <button
                          onClick={() => handleDayBookingCounterOffer(booking.id, booking.flat_rate)}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                          💵 Counter Offer
                        </button>
                        <button
                          onClick={() => setDeclineConfirm({ isOpen: true, id: booking.id, type: 'dayBooking' })}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                        >
                          ✗ Decline
                        </button>
                      </>
                    ) : booking.status === 'counter_offered' ? (
                      <div className="flex items-center gap-3 w-full">
                        <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg font-medium">
                          ⏳ Counter Offer Sent - Waiting for rider response
                        </span>
                        <span className="text-sm text-gray-600">
                          Offered: ${parseFloat(booking.counter_offer_flat_rate).toFixed(2)}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Ride Requests Section */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">🚗 Single Ride Requests</h2>
        {requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 text-lg">No pending ride requests</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(request => (
              <div key={request.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    {request.rider_name && (
                      <div className="text-base font-medium text-blue-600 mb-2">
                        👤 Rider: {request.rider_name}
                      </div>
                    )}
                    <h3 className="text-lg font-bold mb-2">
                      {request.pickup_address} → {request.dropoff_address}
                    </h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>📅 Pickup: {formatDateTime(request.requested_pickup_time, 'full')}</div>
                      <div>📏 Distance: {request.distance_miles} miles</div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-lg">💰 Your Earnings:</span>
                        <span className="text-2xl font-bold text-green-600">
                          ${request.driver_payout?.toFixed(2) || (request.counter_offer_price || request.quoted_price)}
                        </span>
                        <span className="text-xs text-gray-600">(Rider pays: ${request.rider_total?.toFixed(2) || request.quoted_price})</span>
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded text-sm font-medium ${
                    request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    request.status === 'counter_offered' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {request.status.replace('_', ' ').toUpperCase()}
                  </div>
                </div>

                {/* Buffer Time Section - Set buffer times for this ride */}
                <div className="mb-4 border-t pt-4">
                  <button
                    onClick={() => toggleBufferSection(request.id)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      ⏱️ Set Buffer Times {selectedRequest === request.id ? '▼' : '▶'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {bufferTimes[request.id]?.pre_ride_buffer_minutes > 0 || bufferTimes[request.id]?.post_ride_buffer_minutes > 0 
                        ? `Pre: ${bufferTimes[request.id]?.pre_ride_buffer_minutes}min, Post: ${bufferTimes[request.id]?.post_ride_buffer_minutes}min`
                        : 'Optional - Click to set'
                      }
                    </span>
                  </button>

                  {selectedRequest === request.id && (
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-3">
                        Buffer times block your schedule before and after this ride, preventing other bookings during that time.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Pre-ride buffer */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Pre-ride Buffer
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={bufferTimes[request.id]?.pre_ride_buffer_minutes || 0}
                              onChange={(e) => handleBufferChange(request.id, 'pre_ride_buffer_minutes', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                            <span className="text-sm text-gray-600">min</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Time before pickup to prepare (e.g., travel to location, vehicle check)
                          </p>
                        </div>

                        {/* Post-ride buffer */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Post-ride Buffer
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              max="120"
                              value={bufferTimes[request.id]?.post_ride_buffer_minutes || 0}
                              onChange={(e) => handleBufferChange(request.id, 'post_ride_buffer_minutes', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                              placeholder="0"
                            />
                            <span className="text-sm text-gray-600">min</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Time after dropoff to rest, refuel, or handle paperwork
                          </p>
                        </div>
                      </div>

                      {/* Quick presets */}
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-gray-600 mb-2">Quick Presets:</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setBufferTimes(prev => ({
                                ...prev,
                                [request.id]: { pre_ride_buffer_minutes: 15, post_ride_buffer_minutes: 30 }
                              }));
                            }}
                            className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            Standard (15/30)
                          </button>
                          <button
                            onClick={() => {
                              setBufferTimes(prev => ({
                                ...prev,
                                [request.id]: { pre_ride_buffer_minutes: 30, post_ride_buffer_minutes: 45 }
                              }));
                            }}
                            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Extended (30/45)
                          </button>
                          <button
                            onClick={() => {
                              setBufferTimes(prev => ({
                                ...prev,
                                [request.id]: { pre_ride_buffer_minutes: 0, post_ride_buffer_minutes: 0 }
                              }));
                            }}
                            className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            None (0/0)
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Schedule Preview Section */}
                <div className="mb-4 border-t pt-4">
                  <button
                    onClick={() => toggleCalendar(request.id)}
                    className="flex items-center justify-between w-full text-left px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      📅 {expandedCalendars[request.id] ? 'Hide' : 'Show'} Schedule Preview
                    </span>
                    <span className="text-gray-500">
                      {expandedCalendars[request.id] ? '▼' : '▶'}
                    </span>
                  </button>

                  {expandedCalendars[request.id] && (
                    <div className="mt-4">
                      {/* Calendar View */}
                      {calendarLoading[request.id] ? (
                        <div className="bg-gray-50 rounded-lg p-8 text-center">
                          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                          <p className="text-sm text-gray-600">Loading schedule...</p>
                        </div>
                      ) : calendarData[request.id] ? (
                        <MiniCalendar
                          events={calendarData[request.id].events}
                          timeWindow={calendarData[request.id].time_window}
                        />
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500">
                          Unable to load schedule preview
                        </div>
                      )}

                      {/* Travel Time Info */}
                      <TravelTimeInfo
                        travelTimes={travelTimeData[request.id]}
                        loading={travelTimeLoading[request.id]}
                        error={travelTimeErrors[request.id]}
                        isFreeTeir={driverTier === 5}
                      />
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => handleAccept(request.id)}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                  >
                    ✓ Accept Ride
                  </button>
                  <button
                    onClick={() => handleCounterOffer(request.id, request.counter_offer_price || request.quoted_price)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    💵 Counter Offer
                  </button>
                  <button
                    onClick={() => setDeclineConfirm({ isOpen: true, id: request.id, type: 'ride' })}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    ✗ Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ConfirmModal
        isOpen={declineConfirm.isOpen}
        onClose={() => setDeclineConfirm({ isOpen: false, id: null, type: null })}
        onConfirm={() => {
          if (declineConfirm.type === 'dayBooking') {
            handleDayBookingDecline(declineConfirm.id);
          } else {
            handleDecline(declineConfirm.id);
          }
        }}
        title={declineConfirm.type === 'dayBooking' ? 'Decline Day Booking' : 'Decline Ride Request'}
        message={declineConfirm.type === 'dayBooking'
          ? 'Are you sure you want to decline this day booking request?'
          : 'Are you sure you want to decline this ride request?'}
        confirmText="Decline"
        confirmColor="red"
      />
    </div>
  );
};

export default DriverRideRequests;
